import { Period } from '@kwenta/sdk/constants'
import { NetworkId } from '@kwenta/sdk/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { ORDER_PREVIEW_ERRORS } from 'queries/futures/constants'
import {
	DEFAULT_MAP_BY_NETWORK,
	DEFAULT_QUERY_STATUS,
	LOADING_STATUS,
	SUCCESS_STATUS,
	ZERO_STATE_CM_ACCOUNT,
} from 'state/constants'
import { FetchStatus } from 'state/types'

import { PreviewAction } from '../common/types'
import { DelegationAccountInfo } from '../types'

import {
	fetchCrossMarginPendingOrders,
	fetchCrossMarginPositions,
	fetchPositionHistoryV3,
	fetchV3Markets,
	fetchPerpsV3Account,
	fetchAccountMarginInfo,
	fetchAccountCollateralBalances,
	fetchCrossMarginAccountTrades,
	fetchSnxV3GlobalTradesForMarket,
	fetchFundingRatesHistoryV3,
	fetchDelegatesForCMAccount,
	fetchSubAccountsForCMAccount,
	fetchConditionalOrders,
	fetchMarginTransfersV3,
	fetchKeeperBalance,
	fetchDailyVolumesV3,
	fetchPositionHistoryV3ForTrader,
	fetchPnlSnapshotsV3,
	fetchMarginSnapshotsV3,
	fetchPerpsV3GlobalLiquidations,
	fetchCrossMarginAccountLiquidations,
} from './actions'
import { fetchCrossMarginTradePreview, fetchCrossMarginOrderHistory } from './actions'
import { SnxV3AccountData, SnxPerpsV3State, SnxPerpsV3TradePreview, BalanceSpenders } from './types'

export const CROSS_MARGIN_INITIAL_STATE: SnxPerpsV3State = {
	markets: {
		11155420: [],
		10: [],
	},
	dailyMarketVolumes: {},
	accounts: DEFAULT_MAP_BY_NETWORK,
	previews: {
		trade: null,
		close: null,
		edit: null,
	},
	previewDebounceCount: 0,
	perpsV3Spenders: undefined,
	tradeFee: '0',
	fundingRates: [],
	tradesHistory: {},
	liquidationsHistory: {},
	dashboard: {
		selectedPortfolioTimeframe: Period.ONE_WEEK,
	},
	queryStatuses: {
		markets: DEFAULT_QUERY_STATUS,
		dailyVolumes: DEFAULT_QUERY_STATUS,
		positions: DEFAULT_QUERY_STATUS,
		account: DEFAULT_QUERY_STATUS,
		openOrders: DEFAULT_QUERY_STATUS,
		tradePreview: DEFAULT_QUERY_STATUS,
		positionHistory: DEFAULT_QUERY_STATUS,
		trades: DEFAULT_QUERY_STATUS,
		liquidations: DEFAULT_QUERY_STATUS,
		marginTransfers: DEFAULT_QUERY_STATUS,
		historicalFundingRates: DEFAULT_QUERY_STATUS,
		futuresFees: DEFAULT_QUERY_STATUS,
		futuresFeesForAccount: DEFAULT_QUERY_STATUS,
		availableMargin: DEFAULT_QUERY_STATUS,
		globalTradesHistory: DEFAULT_QUERY_STATUS,
		liquidationsHistory: DEFAULT_QUERY_STATUS,
		subAccountsForCMAccount: DEFAULT_QUERY_STATUS,
		delegatesForCMAccount: DEFAULT_QUERY_STATUS,
		conditionalOrders: DEFAULT_QUERY_STATUS,
		orderHistory: DEFAULT_QUERY_STATUS,
		selectedTraderPositionHistory: DEFAULT_QUERY_STATUS,
		pnlSnapshots: DEFAULT_QUERY_STATUS,
		marginSnapshots: DEFAULT_QUERY_STATUS,
	},
	historicalFundingRates: {},
	leaderboard: {
		selectedTraderV3PositionHistory: DEFAULT_MAP_BY_NETWORK,
	},
}

const crossMarginSlice = createSlice({
	name: 'crossMargin',
	initialState: CROSS_MARGIN_INITIAL_STATE,
	reducers: {
		setTradeFee: (state, action: PayloadAction<string>) => {
			state.tradeFee = action.payload
		},
		setPerpsV3Spenders: (state, action: PayloadAction<BalanceSpenders | undefined>) => {
			state.perpsV3Spenders = action.payload
		},
		handlePreviewError: (
			futuresState,
			{
				payload,
			}: PayloadAction<{
				error: string
				previewType: PreviewAction
			}>
		) => {
			const message = Object.values(ORDER_PREVIEW_ERRORS).includes(payload.error)
				? payload.error
				: 'Failed to get trade preview'
			futuresState.queryStatuses.tradePreview = {
				status: FetchStatus.Error,
				error: message,
			}
			futuresState.previews[payload.previewType] = null
		},

		setPerpsV3Account: (
			state,
			action: PayloadAction<{
				wallet: string
				accountId: string
				marginEnginePermitted: boolean
				network: NetworkId
			}>
		) => {
			const { accountId, wallet, network, marginEnginePermitted } = action.payload
			if (!state.accounts[network]?.[wallet]?.account) {
				state.accounts[network] = {
					...state.accounts[network],
					[wallet]: {
						account: accountId,
						marginEnginePermitted,
						...ZERO_STATE_CM_ACCOUNT,
					},
				}
			}
		},
		setCrossMarginTradePreview: (
			state,
			{
				payload,
			}: PayloadAction<{
				preview: SnxPerpsV3TradePreview<string> | null
				type: PreviewAction
			}>
		) => {
			state.previews[payload.type] = payload.preview
		},
		clearAllTradePreviews: (state) => {
			state.previews = {
				edit: null,
				trade: null,
				close: null,
			}
			state.queryStatuses.tradePreview = DEFAULT_QUERY_STATUS
		},
		incrementPreviewCrossMarginCount: (state) => {
			state.previewDebounceCount = state.previewDebounceCount + 1
		},
		setSelectedPortfolioTimeframe: (state, action: PayloadAction<Period>) => {
			state.dashboard.selectedPortfolioTimeframe = action.payload
		},
		addDelegateToCMDelegates: (
			crossMargin,
			action: PayloadAction<{
				wallet: string
				network: NetworkId
				delegate: DelegationAccountInfo
			}>
		) => {
			const { wallet, network, delegate } = action.payload
			const delegates = crossMargin.accounts[network]?.[wallet]?.delegates ?? []
			updateCrossMarginAccount(crossMargin, network, wallet, {
				delegates: [...delegates, { ...delegate }],
			})
		},
		removeDelegateFromCMDelegates: (
			crossMargin,
			action: PayloadAction<{ wallet: string; network: NetworkId; delegatedAddress: string }>
		) => {
			const { wallet, network, delegatedAddress } = action.payload
			const delegates = crossMargin.accounts[network]?.[wallet]?.delegates ?? []
			updateCrossMarginAccount(crossMargin, network, wallet, {
				delegates: delegates?.filter(
					({ address }) => address.toLowerCase() !== delegatedAddress.toLowerCase()
				),
			})
		},
	},
	extraReducers: (builder) => {
		// Markets
		builder.addCase(fetchV3Markets.pending, (crossMargin) => {
			crossMargin.queryStatuses.markets = LOADING_STATUS
		})
		builder.addCase(fetchV3Markets.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.markets = SUCCESS_STATUS
			if (payload) {
				crossMargin.markets[payload.networkId] = payload.markets
			}
		})
		builder.addCase(fetchV3Markets.rejected, (crossMargin) => {
			crossMargin.queryStatuses.markets = {
				status: FetchStatus.Error,
				error: 'Failed to fetch markets',
			}
		})

		// Cross margin positions
		builder.addCase(fetchCrossMarginPositions.pending, (crossMargin) => {
			crossMargin.queryStatuses.positions = LOADING_STATUS
		})
		builder.addCase(fetchCrossMarginPositions.fulfilled, (crossMargin, action) => {
			crossMargin.queryStatuses.positions = SUCCESS_STATUS
			if (!action.payload) return
			const { account, positions, network } = action.payload
			const wallet = findWalletForAccount(crossMargin, account, network)
			if (wallet) {
				updateCrossMarginAccount(crossMargin, network, wallet, { positions })
			}
		})
		builder.addCase(fetchCrossMarginPositions.rejected, (crossMargin) => {
			crossMargin.queryStatuses.positions = {
				status: FetchStatus.Error,
				error: 'Failed to fetch positions',
			}
		})

		// TODO: Refetch cross margin position
		// Refetch selected position
		// builder.addCase(refetchPosition.fulfilled, (futuresState, { payload }) => {
		// 	if (payload) {
		// 		const { position, wallet, networkId } = payload
		// 		const account = futuresState.accounts[networkId]?.[wallet]

		// 		const existingPositions = account?.positions ?? []
		// 		const index = existingPositions.findIndex((p) => p.marketKey === position.marketKey)
		// 		existingPositions[index] = position
		// 		futuresState.accounts[networkId][wallet] = {
		// 			...account,
		// 			positions: existingPositions,
		// 		}
		// 	}
		// })

		// Fetch Isolated Open Orders
		builder.addCase(fetchCrossMarginPendingOrders.pending, (crossMargin) => {
			crossMargin.queryStatuses.openOrders = LOADING_STATUS
		})
		builder.addCase(fetchCrossMarginPendingOrders.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.openOrders = SUCCESS_STATUS
			if (payload) {
				const { order: asyncOrder, wallet, networkId } = payload
				updateCrossMarginAccount(crossMargin, networkId, wallet, {
					pendingAsyncOrder: asyncOrder,
				})
			}
		})
		builder.addCase(fetchCrossMarginPendingOrders.rejected, (crossMargin) => {
			crossMargin.queryStatuses.openOrders = {
				status: FetchStatus.Error,
				error: 'Failed to fetch open orders for isolated margin',
			}
		})

		// Fetch cross margin trade preview
		builder.addCase(fetchCrossMarginTradePreview.pending, (crossMargin) => {
			crossMargin.queryStatuses.tradePreview = LOADING_STATUS
		})
		builder.addCase(fetchCrossMarginTradePreview.fulfilled, (crossMargin, { payload }) => {
			crossMargin.previews[payload.type] = payload.preview
			crossMargin.queryStatuses.tradePreview = SUCCESS_STATUS
		})
		builder.addCase(fetchCrossMarginTradePreview.rejected, (crossMargin) => {
			crossMargin.queryStatuses.tradePreview = {
				status: FetchStatus.Error,
				error: 'Failed to generate trade preview',
			}
		})

		// Fetch cross margin account
		builder.addCase(fetchPerpsV3Account.pending, (crossMargin) => {
			crossMargin.queryStatuses.account = LOADING_STATUS
		})
		builder.addCase(fetchPerpsV3Account.fulfilled, (crossMargin, action) => {
			if (action.payload) {
				const { network, accountId, wallet, marginEnginePermitted } = action.payload

				crossMargin.accounts[network] = {
					...crossMargin.accounts[network],
					[wallet]: {
						...ZERO_STATE_CM_ACCOUNT,
						...crossMargin.accounts[network]?.[wallet],
						account: accountId,
						marginEnginePermitted,
					},
				}
			}
			crossMargin.queryStatuses.account = SUCCESS_STATUS
		})
		builder.addCase(fetchPerpsV3Account.rejected, (crossMargin) => {
			crossMargin.queryStatuses.account = {
				status: FetchStatus.Error,
				error: 'Failed to fetch account',
			}
		})

		builder.addCase(fetchAccountMarginInfo.pending, (crossMargin) => {
			crossMargin.queryStatuses.availableMargin = LOADING_STATUS
		})
		builder.addCase(fetchAccountMarginInfo.fulfilled, (crossMargin, { payload }) => {
			if (payload) {
				const { wallet, marginInfo, network } = payload
				updateCrossMarginAccount(crossMargin, network, wallet, {
					marginInfo,
				})
			}
			crossMargin.queryStatuses.availableMargin = SUCCESS_STATUS
		})
		builder.addCase(fetchAccountMarginInfo.rejected, (crossMargin) => {
			crossMargin.queryStatuses.availableMargin = {
				status: FetchStatus.Error,
				error: 'Failed to fetch available margin',
			}
		})

		// Fetch position history
		builder.addCase(fetchPositionHistoryV3.pending, (crossMargin) => {
			crossMargin.queryStatuses.positionHistory = LOADING_STATUS
		})
		builder.addCase(fetchPositionHistoryV3.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.positionHistory = SUCCESS_STATUS
			if (payload) {
				const { history: positionHistory, networkId, wallet } = payload
				updateCrossMarginAccount(crossMargin, networkId, wallet, {
					positionHistory,
				})
			}
		})
		builder.addCase(fetchPositionHistoryV3.rejected, (crossMargin) => {
			crossMargin.queryStatuses.positionHistory = {
				error: 'Failed to fetch position history',
				status: FetchStatus.Error,
			}
		})

		// Fetch funding rates
		builder.addCase(fetchFundingRatesHistoryV3.rejected, (crossMargin) => {
			crossMargin.queryStatuses.historicalFundingRates = {
				error: 'Failed to fetch funding rates',
				status: FetchStatus.Error,
			}
		})
		builder.addCase(fetchFundingRatesHistoryV3.fulfilled, (crossMargin, { payload }) => {
			crossMargin.historicalFundingRates[payload.marketAsset] = payload.rates
		})

		// Fetch collateral balances
		builder.addCase(fetchAccountCollateralBalances.pending, (crossMargin) => {
			crossMargin.queryStatuses.positionHistory = LOADING_STATUS
		})
		builder.addCase(fetchAccountCollateralBalances.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.positionHistory = SUCCESS_STATUS
			if (payload) {
				const { balancesMap, network, wallet } = payload
				updateCrossMarginAccount(crossMargin, network, wallet, {
					collateralBalances: balancesMap,
				})
			}
		})
		builder.addCase(fetchAccountCollateralBalances.rejected, (crossMargin) => {
			crossMargin.queryStatuses.positionHistory = {
				error: 'Failed to fetch position history',
				status: FetchStatus.Error,
			}
		})

		// Fetch cross margin trades
		builder.addCase(fetchCrossMarginAccountTrades.pending, (crossMargin) => {
			crossMargin.queryStatuses.trades = LOADING_STATUS
		})
		builder.addCase(fetchCrossMarginAccountTrades.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.trades = SUCCESS_STATUS
			if (payload) {
				const { trades, networkId, wallet } = payload
				updateCrossMarginAccount(crossMargin, networkId, wallet, {
					trades,
				})
			}
		})
		builder.addCase(fetchCrossMarginAccountTrades.rejected, (crossMargin) => {
			crossMargin.queryStatuses.trades = {
				error: 'Failed to fetch position history',
				status: FetchStatus.Error,
			}
		})

		// Fetch global trade history
		builder.addCase(fetchSnxV3GlobalTradesForMarket.pending, (crossMargin) => {
			crossMargin.queryStatuses.globalTradesHistory = LOADING_STATUS
		})
		builder.addCase(fetchSnxV3GlobalTradesForMarket.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.globalTradesHistory = SUCCESS_STATUS
			if (payload) {
				const { trades, networkId, marketId } = payload
				const existingTrades = crossMargin.tradesHistory[networkId]?.[marketId] ?? []
				const allTrades = [...trades, ...existingTrades]
				const uniqueTrades = allTrades
					.filter((trade, index, self) => index === self.findIndex((t) => t.id === trade.id))
					.sort((a, b) => b.timestamp - a.timestamp)
				crossMargin.tradesHistory[networkId] = {
					...crossMargin.tradesHistory[networkId],
					[marketId]: uniqueTrades,
				}
			}
		})
		builder.addCase(fetchSnxV3GlobalTradesForMarket.rejected, (crossMargin) => {
			crossMargin.queryStatuses.globalTradesHistory = {
				error: 'Failed to fetch global trade history',
				status: FetchStatus.Error,
			}
		})

		// Fetch cross margin liquidations
		builder.addCase(fetchCrossMarginAccountLiquidations.pending, (crossMargin) => {
			crossMargin.queryStatuses.liquidations = LOADING_STATUS
		})
		builder.addCase(fetchCrossMarginAccountLiquidations.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.liquidations = SUCCESS_STATUS
			if (payload) {
				const { liquidations, networkId, wallet } = payload
				updateCrossMarginAccount(crossMargin, networkId, wallet, {
					liquidations,
				})
			}
		})
		builder.addCase(fetchCrossMarginAccountLiquidations.rejected, (crossMargin) => {
			crossMargin.queryStatuses.liquidations = {
				error: 'Failed to fetch liquidations',
				status: FetchStatus.Error,
			}
		})
		// fetch global liquidations
		builder.addCase(fetchPerpsV3GlobalLiquidations.pending, (crossMargin) => {
			crossMargin.queryStatuses.liquidationsHistory = LOADING_STATUS
		})
		builder.addCase(fetchPerpsV3GlobalLiquidations.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.liquidationsHistory = SUCCESS_STATUS
			if (payload) {
				const { liquidations, networkId, marketId } = payload
				const existingLiquidations = crossMargin.liquidationsHistory[networkId]?.[marketId] ?? []
				const allLiquidations = [...liquidations, ...existingLiquidations]
				const uniqueLiquidations = allLiquidations
					.filter(
						(liquidation, index, self) => index === self.findIndex((l) => l.id === liquidation.id)
					)
					.sort((a, b) => b.timestamp - a.timestamp)
				crossMargin.liquidationsHistory[networkId] = {
					...crossMargin.liquidationsHistory[networkId],
					[marketId]: uniqueLiquidations,
				}
			}
		})
		builder.addCase(fetchPerpsV3GlobalLiquidations.rejected, (crossMargin) => {
			crossMargin.queryStatuses.liquidationsHistory = {
				error: 'Failed to fetch liquidation history',
				status: FetchStatus.Error,
			}
		})

		// manage delegates
		builder.addCase(fetchDelegatesForCMAccount.pending, (futuresState) => {
			futuresState.queryStatuses.delegatesForCMAccount = LOADING_STATUS
		})
		builder.addCase(fetchDelegatesForCMAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.delegatesForCMAccount = SUCCESS_STATUS
			if (payload) {
				const { network, wallet, delegatesWithNicknames } = payload
				updateCrossMarginAccount(futuresState, network, wallet, {
					delegates: delegatesWithNicknames,
				})
			}
		})
		builder.addCase(fetchDelegatesForCMAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.delegatesForCMAccount = {
				status: FetchStatus.Error,
				error: 'Failed to fetch delegates',
			}
		})

		// manage sub-accounts
		builder.addCase(fetchSubAccountsForCMAccount.pending, (futuresState) => {
			futuresState.queryStatuses.subAccountsForCMAccount = LOADING_STATUS
		})
		builder.addCase(fetchSubAccountsForCMAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.subAccountsForCMAccount = SUCCESS_STATUS
			if (payload) {
				const { network, wallet, subAccountsWithNicknames } = payload
				updateCrossMarginAccount(futuresState, network, wallet, {
					subAccounts: subAccountsWithNicknames,
				})
			}
		})
		builder.addCase(fetchSubAccountsForCMAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.subAccountsForCMAccount = {
				status: FetchStatus.Error,
				error: 'Failed to fetch delegates',
			}
		})
		// Fetch cond orders
		builder.addCase(fetchConditionalOrders.pending, (crossMargin) => {
			crossMargin.queryStatuses.conditionalOrders = LOADING_STATUS
		})
		builder.addCase(fetchConditionalOrders.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.conditionalOrders = SUCCESS_STATUS
			if (!payload) return
			if (payload) {
				const { orders, networkId, wallet } = payload
				updateCrossMarginAccount(crossMargin, networkId, wallet, {
					conditionalOrders: orders,
				})
			}
		})
		builder.addCase(fetchConditionalOrders.rejected, (crossMargin) => {
			crossMargin.queryStatuses.conditionalOrders = {
				error: 'Failed to fetch cross margin conditional orders',
				status: FetchStatus.Error,
			}
		})

		// Fetch Eth balance
		builder.addCase(fetchKeeperBalance.fulfilled, (crossMargin, { payload }) => {
			if (!payload) return
			if (payload) {
				const { balance, network, wallet } = payload
				updateCrossMarginAccount(crossMargin, network, wallet, {
					usdcBalance: balance,
				})
			}
		})

		// Daily market volumes
		builder.addCase(fetchDailyVolumesV3.pending, (crossMargin) => {
			crossMargin.queryStatuses.dailyVolumes = LOADING_STATUS
		})
		builder.addCase(fetchDailyVolumesV3.fulfilled, (crossMargin, action) => {
			crossMargin.queryStatuses.dailyVolumes = SUCCESS_STATUS
			crossMargin.dailyMarketVolumes = action.payload
		})
		builder.addCase(fetchDailyVolumesV3.rejected, (crossMargin) => {
			crossMargin.queryStatuses.dailyVolumes = {
				status: FetchStatus.Error,
				error: 'Failed to fetch volume data',
			}
		})

		//cross margin transfers
		builder.addCase(fetchMarginTransfersV3.pending, (crossMargin) => {
			crossMargin.queryStatuses.marginTransfers = LOADING_STATUS
		})
		builder.addCase(fetchMarginTransfersV3.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.marginTransfers = SUCCESS_STATUS
			if (payload) {
				const { context, accountTransfers } = payload
				const newAccountData = {
					accountTransfers,
				}
				updateCrossMarginAccount(crossMargin, context.network, context.wallet, newAccountData)
			}
		})
		builder.addCase(fetchMarginTransfersV3.rejected, (crossMargin) => {
			crossMargin.queryStatuses.marginTransfers = {
				status: FetchStatus.Error,
				error: 'Failed to fetch cross margin transfers',
			}
		})

		// Fetch all orders for account
		builder.addCase(fetchCrossMarginOrderHistory.pending, (futuresState) => {
			futuresState.queryStatuses.orderHistory = LOADING_STATUS
		})
		builder.addCase(fetchCrossMarginOrderHistory.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.orderHistory = SUCCESS_STATUS
			if (payload) {
				const { orders: orderHistory, networkId, wallet } = payload
				updateCrossMarginAccount(futuresState, networkId, wallet, {
					orderHistory,
				})
			}
		})
		builder.addCase(fetchCrossMarginOrderHistory.rejected, (futuresState) => {
			futuresState.queryStatuses.orderHistory = {
				error: 'Failed to fetch orders',
				status: FetchStatus.Error,
			}
		})

		// Fetch v3 position history for trader
		builder.addCase(fetchPositionHistoryV3ForTrader.pending, (futuresState) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = LOADING_STATUS
		})
		builder.addCase(fetchPositionHistoryV3ForTrader.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = SUCCESS_STATUS
			if (!payload) return
			futuresState.leaderboard.selectedTraderV3PositionHistory[payload.networkId] = {
				...futuresState.leaderboard.selectedTraderV3PositionHistory[payload.networkId],
				[payload.address]: payload.history,
			}
		})
		builder.addCase(fetchPositionHistoryV3ForTrader.rejected, (futuresState) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = {
				error: 'Failed to fetch traders position history',
				status: FetchStatus.Error,
			}
		})
		// Fetch v3 pnl snapshots
		builder.addCase(fetchPnlSnapshotsV3.pending, (futuresState) => {
			futuresState.queryStatuses.pnlSnapshots = LOADING_STATUS
		})
		builder.addCase(fetchPnlSnapshotsV3.rejected, (futuresState) => {
			futuresState.queryStatuses.pnlSnapshots = {
				error: 'Failed to fetch pnl shanpshots',
				status: FetchStatus.Error,
			}
		})
		builder.addCase(fetchPnlSnapshotsV3.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.pnlSnapshots = SUCCESS_STATUS
			if (payload) {
				const { networkId, wallet, snapshots } = payload
				updateCrossMarginAccount(crossMargin, networkId, wallet, {
					pnlSnapshots: snapshots,
				})
			}
		})

		// Fetch v3 margin snapshots
		builder.addCase(fetchMarginSnapshotsV3.pending, (futuresState) => {
			futuresState.queryStatuses.marginSnapshots = LOADING_STATUS
		})
		builder.addCase(fetchMarginSnapshotsV3.rejected, (futuresState) => {
			futuresState.queryStatuses.marginSnapshots = {
				error: 'Failed to fetch margin shanpshots',
				status: FetchStatus.Error,
			}
		})
		builder.addCase(fetchMarginSnapshotsV3.fulfilled, (crossMargin, { payload }) => {
			crossMargin.queryStatuses.marginSnapshots = SUCCESS_STATUS
			if (payload) {
				const { networkId, wallet, snapshots } = payload
				updateCrossMarginAccount(crossMargin, networkId, wallet, {
					marginSnapshots: snapshots,
				})
			}
		})
	},
})

export default crossMarginSlice.reducer

export const {
	handlePreviewError,
	clearAllTradePreviews,
	setSelectedPortfolioTimeframe,
	setPerpsV3Account,
	setPerpsV3Spenders,
	setCrossMarginTradePreview,
	incrementPreviewCrossMarginCount,
	addDelegateToCMDelegates,
	removeDelegateFromCMDelegates,
} = crossMarginSlice.actions

const findWalletForAccount = (
	perpsV3State: SnxPerpsV3State,
	account: string,
	network: NetworkId
) => {
	const entry = Object.entries(perpsV3State.accounts[network]).find(([_, value]) => {
		return value.account === account
	})
	return entry ? entry[0] : undefined
}

const updateCrossMarginAccount = (
	futuresState: SnxPerpsV3State,
	network: NetworkId,
	wallet: string,
	newAccountData: Partial<SnxV3AccountData>
) => {
	const updatedAccount = {
		...ZERO_STATE_CM_ACCOUNT,
		...futuresState.accounts[network]?.[wallet],
		...newAccountData,
	}

	futuresState.accounts[network] = {
		...futuresState.accounts[network],
		[wallet]: updatedAccount,
	}
}
